import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  User,
  Building,
  Mail,
  Phone,
  Globe,
  Clock,
  DollarSign,
  FileText,
  Download,
  Edit,
  Trash2,
  MapPin,
  Briefcase,
  Target,
  CheckCircle,
  Package
} from 'lucide-react';
import doiService from '../../../api/crm/doi/doiService';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { toast } from 'react-hot-toast';
import PermissionGate from '../../../components/common/PermissionGate';

const DoiView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const doiId = searchParams.get('doiId');

  // Fetch DOI details
  const {
    data: doiData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['doi', doiId],
    queryFn: async () => {
      const result = await doiService.getDoiById(doiId);
      if (result.success) {
        return result.doi;
      } else {
        throw new Error(result.error || 'Failed to fetch DOI');
      }
    },
    enabled: !!doiId,
    retry: 2
  });

  const doi = doiData || {};

  const handleEdit = () => {
    navigate(`/crm/quotation-proposal/quote-builder?doiId=${doiId}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this DOI?')) {
      const result = await doiService.deleteDoi(doiId);
      if (result.success) {
        toast.success('DOI deleted successfully');
        navigate('/crm/quotation-proposal/create-quotes');
      } else {
        toast.error(result.error || 'Failed to delete DOI');
      }
    }
  };

  const handleBack = () => {
    navigate('/crm/quotation-proposal/create-quotes');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (isError || !doi) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-600 text-xl mb-4">Failed to load DOI</div>
        <p className="text-gray-600 mb-4">{error?.message || 'DOI not found'}</p>
        <button onClick={handleBack} className="btn-primary">
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{doi.doiNumber}</h1>
                <p className="text-sm text-gray-500 mt-1">Date of Initiation</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${doi.status === 'created'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
                }`}>
                {doi.status || 'N/A'}
              </span>
              <PermissionGate permission="quotation_management" action="update">
                <button
                  onClick={handleEdit}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit</span>
                </button>
              </PermissionGate>
              <PermissionGate permission="quotation_management" action="delete">
                <button
                  onClick={handleDelete}
                  className="btn-danger flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-brand" />
                Project Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Project Name" value={doi.projectName} />
                <InfoItem label="Project Type" value={doi.projectType} />
                <InfoItem label="Project Start Stage" value={doi.projectStartStage} />
                <InfoItem label="Code Base" value={doi.codeBase} />
                <InfoItem label="Website/Services" value={doi.websiteWebServices} />
                <InfoItem label="Project Website" value={doi.projectWebsite} link />
                <InfoItem label="Estimation (Man Hours)" value={doi.estimationManHours} />
                <InfoItem label="Timeline (Man Days)" value={doi.totalTimelineManDays} />
              </div>

              {doi.platformDomains && doi.platformDomains.length > 0 && (
                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Platform/Domains</label>
                  <div className="flex flex-wrap gap-2">
                    {doi.platformDomains.map((platform, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-50 text-[#0B2033] rounded-full text-sm">
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building className="h-5 w-5 mr-2 text-brand" />
                Customer Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem
                  label="Customer Name"
                  value={doi.mainCustomerNames || doi.customerId?.companyName}
                />
                <InfoItem label="Email" value={doi.customerEmail} icon={<Mail className="h-4 w-4" />} />
                <InfoItem label="Skype ID" value={doi.customerSkypeId} />
                <InfoItem label="WhatsApp" value={doi.customerWhatsappId} icon={<Phone className="h-4 w-4" />} />
                <InfoItem label="Location" value={doi.clientLocation} icon={<MapPin className="h-4 w-4" />} />
                <InfoItem label="Time Zone" value={doi.timeZone} icon={<Clock className="h-4 w-4" />} />
              </div>
            </div>

            {/* Project Details */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-brand" />
                Project Details
              </h2>

              {doi.projectRequirementOverview && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Requirement Overview</label>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{doi.projectRequirementOverview}</p>
                </div>
              )}

              {doi.detailedEstimation && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Detailed Estimation</label>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{doi.detailedEstimation}</p>
                </div>
              )}

              {doi.sow && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Statement of Work (SOW)</label>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{doi.sow}</p>
                </div>
              )}

              {doi.risksAndAssumptions && (
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Risks & Assumptions</label>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{doi.risksAndAssumptions}</p>
                </div>
              )}

              {doi.domainWiseValueDistribution && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Domain-wise Value Distribution</label>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{doi.domainWiseValueDistribution}</p>
                </div>
              )}
            </div>

            {/* Milestones */}
            {doi.milestones && doi.milestones.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Target className="h-5 w-5 mr-2 text-brand" />
                  Milestones
                </h2>
                <div className="space-y-3">
                  {doi.milestones.map((milestone, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{milestone.name}</h3>
                          {milestone.description && (
                            <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                          )}
                          {milestone.deliveryDate && (
                            <p className="text-sm text-gray-500 mt-2 flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {formatDate(milestone.deliveryDate, { includeTime: false })}
                            </p>
                          )}
                        </div>
                        {milestone.amount && (
                          <div className="text-right ml-4">
                            <p className="font-semibold text-gray-900">
                              ₹{formatIndianAmount(milestone.amount)}
                            </p>
                            <p className="text-xs text-gray-500">{milestone.currency || 'INR'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {doi.attachments && doi.attachments.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="h-5 w-5 mr-2 text-brand" />
                  Attachments
                </h2>
                <div className="space-y-2">
                  {doi.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                          <p className="text-xs text-gray-500">
                            {attachment.mimeType} • {(attachment.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <Download className="h-4 w-4 text-gray-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-brand" />
                Financial Summary
              </h2>
              <div className="space-y-4">
                <div className="border-b border-gray-200 pb-3">
                  <label className="text-sm text-gray-600">Project Order Value</label>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    ₹{formatIndianAmount(doi.projectOrderValue?.amount || 0)}
                  </p>
                  <p className="text-xs text-gray-500">{doi.projectOrderValue?.currency || 'INR'}</p>
                </div>

                {doi.advanceAmount?.amount > 0 && (
                  <div className="border-b border-gray-200 pb-3">
                    <label className="text-sm text-gray-600">Advance Amount</label>
                    <p className="text-xl font-semibold text-green-600 mt-1">
                      ₹{formatIndianAmount(doi.advanceAmount.amount)}
                    </p>
                    <p className="text-xs text-gray-500">{doi.advanceAmount.currency || 'INR'}</p>
                  </div>
                )}

                {doi.domainWiseValueDistribution && (
                  <div className="border-b border-gray-200 pb-3">
                    <label className="text-sm text-gray-600">Domain-wise Value Distribution</label>
                    <p className="text-sm text-gray-900 mt-1">{doi.domainWiseValueDistribution}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-gray-600">Estimation (Man Hours)</label>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {doi.estimationManHours || 0} hours
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-600">Timeline (Man Days)</label>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {doi.totalTimelineManDays || 0} days
                  </p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-brand" />
                Timeline
              </h2>
              <div className="space-y-3">
                {doi.leadDate && (
                  <TimelineItem
                    label="Lead Date"
                    date={doi.leadDate}
                  />
                )}
                {doi.doiDate && (
                  <TimelineItem
                    label="DOI Date"
                    date={doi.doiDate}
                  />
                )}
                {doi.finalDeliveryDate && (
                  <TimelineItem
                    label="Final Delivery Date"
                    date={doi.finalDeliveryDate}
                    highlight
                  />
                )}
                {doi.createdAt && (
                  <TimelineItem
                    label="Created"
                    date={doi.createdAt}
                  />
                )}
                {doi.updatedAt && (
                  <TimelineItem
                    label="Last Updated"
                    date={doi.updatedAt}
                  />
                )}
              </div>
            </div>

            {/* Team */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-brand" />
                Team
              </h2>
              <div className="space-y-3">
                {doi.salesRepUserId && (
                  <TeamMember
                    label="Sales Representative"
                    user={doi.salesRepUserId}
                  />
                )}
                {doi.leadGeneratorUserId && (
                  <TeamMember
                    label="Lead Generator"
                    user={doi.leadGeneratorUserId}
                  />
                )}
                {doi.leadSourceId && (
                  <div>
                    <label className="text-xs text-gray-500">Lead Source</label>
                    <p className="text-sm font-medium text-gray-900">
                      {doi.leadSourceId.name || 'N/A'}
                    </p>
                  </div>
                )}
                {doi.estimationApprovedByUsers && doi.estimationApprovedByUsers.length > 0 && (
                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">Estimation Approved By</label>
                    <div className="space-y-2">
                      {doi.estimationApprovedByUsers.map((user, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-gray-900">
                            {user.firstName} {user.lastName}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {doi.createdBy && (
                  <TeamMember
                    label="Created By"
                    user={doi.createdBy}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoItem = ({ label, value, icon, link }) => (
  <div>
    <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
    <div className="flex items-center space-x-2">
      {icon && <span className="text-gray-400">{icon}</span>}
      {link && value ? (
        <a
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#0F2942] hover:underline"
        >
          {value}
        </a>
      ) : (
        <p className="text-sm text-gray-900">{value || 'N/A'}</p>
      )}
    </div>
  </div>
);

const TimelineItem = ({ label, date, highlight }) => (
  <div className={`flex items-center justify-between ${highlight ? 'bg-blue-50 -mx-2 px-2 py-1 rounded' : ''}`}>
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-sm font-medium ${highlight ? 'text-[#0B2033]' : 'text-gray-900'}`}>
      {formatDate(date, { includeTime: false })}
    </span>
  </div>
);

const TeamMember = ({ label, user }) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <p className="text-sm font-medium text-gray-900">
      {user.firstName} {user.lastName}
    </p>
    {user.email && (
      <p className="text-xs text-gray-500">{user.email}</p>
    )}
  </div>
);

export default DoiView;
